/*
 * 0.88: Karten in ein BESTEHENDES Board importieren (Einfügen ins Feld „Karte hinzufügen“ und
 * Dialog „Karten importieren …“). Der Plan kommt aus denselben Lesern wie beim neuen Board
 * (boardImport.ts); hier wird er den Spalten und Labels des Boards zugeordnet. Browser (Vorschau)
 * und Server (verbindlich) rechnen mit derselben Funktion.
 */
import { parseTextImport, looksLikeTable, type ParsedImport } from './boardImport.js';
import type { ImportPlan } from './trello.js';

/** Höchstzahl Karten je Import in ein bestehendes Board (jede Karte läuft wie von Hand angelegt) */
export const CARD_IMPORT_MAX = 500;
/** Spaltentitel neuer Spalten (wie im Spaltendialog) */
const COLUMN_TITLE_MAX = 80;

/** Namen vergleichen: Groß/klein, Leerraum und Unicode-Form egal */
export const importNameKey = (s: string) => s.normalize('NFC').trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Eingefügter Text im Feld „Karte hinzufügen“: Sind es mehrere Aufgaben? Ab zwei Karten
 * (Zeilen oder Tabellenzeilen) gibt es den Plan, sonst null (dann bleibt es eine Karte).
 */
export function pastedTasks(text: string, now = new Date()): ParsedImport | null {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2 && !looksLikeTable(text)) return null;
  const parsed = parseTextImport(text, now);
  return parsed.plan.cards.length >= 2 ? parsed : null;
}

/**
 * Bringt der Plan eigene Spalten mit (Überschriften im Text, Spalte/Status/Bucket in der Tabelle)?
 * Ein einfacher Text ohne Überschriften hat nur die Ersatzspalte „Offen“ – dann gehört alles in
 * die gewählte Zielspalte, auch wenn das Board zufällig eine Spalte „Offen“ hat.
 */
export function hasSourceColumns(parsed: Pick<ParsedImport, 'plan' | 'mapping'>): boolean {
  if (parsed.mapping?.some((m) => m.field === 'column')) return true;
  const cols = parsed.plan.columns.filter((c) => parsed.plan.cards.some((k) => k.columnKey === c.key));
  return !(cols.length <= 1 && (cols[0]?.title ?? 'Offen') === 'Offen');
}

export interface CardImportTargets {
  /** Plan-Spalte → bestehende Spalte (id) oder neue Spalte (Titel) */
  columnFor: Record<string, { id: string } | { newTitle: string }>;
  /** neue Spalten in Plan-Reihenfolge (ohne Dubletten) */
  newColumns: string[];
  /** Plan-Label → bestehendes Label (id) oder neues Label */
  labelFor: Record<string, { id: string } | { newName: string; color: ImportPlan['labels'][number]['color'] }>;
  newLabels: { name: string; color: ImportPlan['labels'][number]['color'] }[];
  /** Karten je Ziel (Schlüssel: Spalten-ID oder `neu:Titel`) – für die Vorschau */
  counts: { target: string; title: string; isNew: boolean; cards: number }[];
}

/**
 * Plan → Board. `useSourceColumns`: Spalten des Plans per Namen zuordnen, fehlende neu anlegen;
 * sonst landen alle Karten in `columnId`. Labels werden immer per Namen zugeordnet, fehlende neu angelegt.
 */
export function matchImportTargets(
  plan: Pick<ImportPlan, 'columns' | 'labels' | 'cards'>,
  board: { columns: { id: string; title: string }[]; labels: { id: string; name: string }[] },
  opts: { columnId: string; useSourceColumns: boolean },
): CardImportTargets {
  const colByName = new Map<string, string>();
  for (const c of board.columns) if (!colByName.has(importNameKey(c.title))) colByName.set(importNameKey(c.title), c.id);
  const columnFor: CardImportTargets['columnFor'] = {};
  const newColumns: string[] = [];
  const used = new Set(plan.cards.map((c) => c.columnKey));
  for (const c of plan.columns) {
    if (!opts.useSourceColumns) {
      columnFor[c.key] = { id: opts.columnId };
      continue;
    }
    const title = c.title.trim().slice(0, COLUMN_TITLE_MAX) || 'Offen';
    const hit = colByName.get(importNameKey(title));
    if (hit) columnFor[c.key] = { id: hit };
    else {
      columnFor[c.key] = { newTitle: title };
      // Nur Spalten anlegen, in die auch Karten kommen
      if (used.has(c.key) && !newColumns.some((n) => importNameKey(n) === importNameKey(title))) newColumns.push(title);
    }
  }

  const labelByName = new Map<string, string>();
  for (const l of board.labels) if (l.name.trim() && !labelByName.has(importNameKey(l.name))) labelByName.set(importNameKey(l.name), l.id);
  const usedLabels = new Set(plan.cards.flatMap((c) => c.labelKeys));
  const labelFor: CardImportTargets['labelFor'] = {};
  const newLabels: CardImportTargets['newLabels'] = [];
  for (const l of plan.labels) {
    const name = l.name.trim().slice(0, 40);
    if (!name) continue;
    const hit = labelByName.get(importNameKey(name));
    if (hit) labelFor[l.key] = { id: hit };
    else {
      const twin = newLabels.find((n) => importNameKey(n.name) === importNameKey(name));
      labelFor[l.key] = { newName: twin?.name ?? name, color: twin?.color ?? l.color };
      if (!twin && usedLabels.has(l.key)) newLabels.push({ name, color: l.color });
    }
  }

  const titleOf = new Map(board.columns.map((c) => [c.id, c.title]));
  const counts = new Map<string, CardImportTargets['counts'][number]>();
  for (const card of plan.cards) {
    const t = columnFor[card.columnKey] ?? { id: opts.columnId };
    const key = 'id' in t ? t.id : `neu:${importNameKey(t.newTitle)}`;
    const entry = counts.get(key) ?? { target: key, title: 'id' in t ? (titleOf.get(t.id) ?? '') : t.newTitle, isNew: !('id' in t), cards: 0 };
    entry.cards++;
    counts.set(key, entry);
  }
  return { columnFor, newColumns, labelFor, newLabels, counts: [...counts.values()] };
}
